import { useMemo } from 'react';
import { isToday } from 'date-fns';
import { getCurrentTimeContext } from './get-current-time-context';
import { getStaticTimeOptions } from './get-static-time-options';
import { MinuteIncrement } from './types';
import { getHourOptionsFromNow } from './get-hour-options-from-now';
import { getMinuteOptionsFromNow } from './get-minute-options-from-now';

export type UseTimeDropdownOptionsOptions = {
	/**
	 * The minute increment for dropdown options (must be a factor of 60).
	 * Common values: 1, 5, 10, 15, 20, 30
	 * @default 15
	 */
	minuteIncrement?: MinuteIncrement;
	/**
	 * Whether to hide past times and show only future options.
	 * When true, only works if selectedDate is today - otherwise returns static options.
	 * @default false
	 */
	hidePastTimes?: boolean;
	/**
	 * The selected date in ISO string format (e.g., "2024-01-15T00:00:00.000Z").
	 * Used with hidePastTimes to determine if dynamic filtering should apply.
	 * Only dates that are "today" will trigger dynamic behavior.
	 */
	selectedDate?: string;
	/**
	 * The currently selected hour (e.g., "14", "09").
	 * Only used when hidePastTimes is true and selectedDate is today.
	 * Affects which minutes are available for the selected hour.
	 */
	selectedHour?: string;
};

/**
 * Custom hook that provides time dropdown options for hour and minute selection.
 *
 * **Behavior:**
 * - **Static mode**: Returns all hours (00-23) and all minute increments
 * - **Dynamic mode**: Filters out past times, starts hours from current hour, restricts minutes based on current time
 *
 * **Performance Notes:**
 * - Static options are heavily memoized and won't cause re-renders
 * - Dynamic options will re-calculate when current time changes or dependencies change
 * - The hook automatically handles the decision between static vs dynamic options
 *
 * @param options - Configuration object for the time dropdown options
 * @returns Object containing time dropdown options with `hours` and `minutes` arrays
 * @throws {TypeError} When minuteIncrement is not a factor of 60
 *
 * @example
 * ```tsx
 * // Basic usage - static options (most common)
 * const { hours, minutes } = useTimeDropdownOptions({
 *   minuteIncrement: 15
 * });
 * // Returns: hours (00-23), minutes (00, 15, 30, 45)
 * ```
 *
 * @example
 * ```tsx
 * // Dynamic options - hide past times for today only
 * const { hours, minutes } = useTimeDropdownOptions({
 *   minuteIncrement: 30,
 *   hidePastTimes: true,
 *   selectedDate: "2024-01-15T00:00:00.000Z", // Today's date
 *   selectedHour: "14" // Currently selected hour
 * });
 * // If current time is 14:25:
 * // Returns: hours (14-23), minutes (30) - only future times
 * ```
 *
 * @example
 * ```tsx
 * // Dynamic options but for future date - falls back to static
 * const { hours, minutes } = useTimeDropdownOptions({
 *   minuteIncrement: 15,
 *   hidePastTimes: true,
 *   selectedDate: "2024-01-16T00:00:00.000Z", // Tomorrow's date
 * });
 * // Returns: hours (00-23), minutes (00, 15, 30, 45) - all options since not today
 * ```
 *
 * @example
 * ```tsx
 * // Real-world usage in a form component
 * const CancellationForm = () => {
 *   const [formData, setFormData] = useState({
 *     date: '',
 *     hour: '',
 *     minute: ''
 *   });
 *
 *   const timeOptions = useTimeDropdownOptions({
 *     minuteIncrement: 15,
 *     hidePastTimes: true,
 *     selectedDate: formData.date,
 *     selectedHour: formData.hour,
 *   });
 *
 *   // timeOptions.hours and timeOptions.minutes will update
 *   // automatically based on form state and current time
 * };
 * ```
 */
export const useTimeDropdownOptions = (options: UseTimeDropdownOptionsOptions = {}) => {
	const { minuteIncrement = 15, hidePastTimes = false, selectedDate, selectedHour } = options;

	// Validate minuteIncrement at hook call time
	if (60 % minuteIncrement !== 0)
		throw new TypeError(`minuteIncrement must be a factor of 60, received ${minuteIncrement}`);

	// Memoize static options - these never change for a given increment
	const staticTimeOptions = useMemo(() => getStaticTimeOptions(minuteIncrement), [minuteIncrement]);

	// Determine if we should use dynamic filtering
	const shouldUseDynamicFiltering = useMemo(
		() => hidePastTimes && selectedDate && isToday(selectedDate),
		[hidePastTimes, selectedDate],
	);

	return useMemo(() => {
		// Use dynamic filtering only if:
		// 1. hidePastTimes is true
		// 2. selectedDate is provided
		// 3. selectedDate is today (not past or future dates)
		if (shouldUseDynamicFiltering) {
			const timeContext = getCurrentTimeContext(minuteIncrement);
			return {
				hours: getHourOptionsFromNow(timeContext),
				minutes: getMinuteOptionsFromNow(timeContext, minuteIncrement, selectedHour),
			};
		}

		// Default behavior: return all static options
		return staticTimeOptions;
	}, [shouldUseDynamicFiltering, minuteIncrement, selectedHour, staticTimeOptions]);
};

/**
 * Type representing the return value of useTimeDropdownOptions hook.
 * Contains arrays of hour and minute options formatted for dropdown components.
 */
export type TimeDropdownOptions = ReturnType<typeof useTimeDropdownOptions>;
